import React, { useState, useMemo } from 'react';
import { ArrowRight, Link as LinkIcon, Unlink, CheckCircle2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { CventRfpSearchCombobox } from './CventRfpSearchCombobox';
import { DisconnectCventDialog } from './DisconnectCventDialog';
import { StepHeader } from '../StepHeader';
import { CventIntegrationService, type CventRfp } from '@/services/CventIntegrationService';
import { useNotificationStore } from '@/stores/notificationStore';
import { cn } from '@/lib/utils';

interface ConnectedRfpInfo {
  id: string;
  name: string;
  code: string;
  connectedAt: string;
  supplierCount?: number; // Number of suppliers/venues in the RFP
  lastSyncedAt?: string | null; // Timestamp when data was last synced
}

interface ReviewHeaderProps {
  eventId: string;
  totalVenues: number;
  destinationCount: number;
  connectedRfp: ConnectedRfpInfo | null;
  onBack: () => void;
  onRfpConnected: (matchResults?: any) => void;
  onRfpDisconnected: () => void;
  onRefresh?: () => Promise<void>; // Callback for manual refresh
  onContinueToSelection?: () => void;
  submittedProposalCount?: number;
}

/**
 * Format time difference in a human-readable way
 */
function formatTimeAgo(timestamp: string | null | undefined): string {
  if (!timestamp) return 'Never';
  
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
}

/**
 * Calculate next sync time (30 minutes after last sync)
 */
function getNextSyncTime(lastSyncedAt: string | null | undefined): string | null {
  if (!lastSyncedAt) return null;
  
  const SYNC_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
  const lastSynced = new Date(lastSyncedAt).getTime();
  const nextSync = lastSynced + SYNC_INTERVAL_MS;
  const now = Date.now();
  
  if (nextSync <= now) return null; // Should sync now
  
  const diffMs = nextSync - now;
  const diffMins = Math.ceil(diffMs / 60000);
  
  if (diffMins < 60) return `in ${diffMins} minute${diffMins === 1 ? '' : 's'}`;
  
  const diffHours = Math.floor(diffMins / 60);
  const remainingMins = diffMins % 60;
  
  if (remainingMins === 0) {
    return `in ${diffHours} hour${diffHours === 1 ? '' : 's'}`;
  }
  
  return `in ${diffHours}h ${remainingMins}m`;
}

export function ReviewHeader({
  eventId,
  totalVenues,
  destinationCount,
  connectedRfp,
  onBack,
  onRfpConnected,
  onRfpDisconnected,
  onRefresh,
  onContinueToSelection,
  submittedProposalCount = 0,
}: ReviewHeaderProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  const cventService = new CventIntegrationService();
  const notificationStore = useNotificationStore();

  // Calculate sync status display
  const syncStatus = useMemo(() => {
    if (!connectedRfp?.lastSyncedAt) {
      return {
        lastSync: 'Never synced',
        nextSync: null,
        shouldSyncNow: true,
      };
    }
    
    const lastSync = formatTimeAgo(connectedRfp.lastSyncedAt);
    const nextSync = getNextSyncTime(connectedRfp.lastSyncedAt);
    const shouldSyncNow = !nextSync; // If nextSync is null, we should sync now
    
    return {
      lastSync: `Last synced: ${lastSync}`,
      nextSync: nextSync ? `Next sync ${nextSync}` : null,
      shouldSyncNow,
    };
  }, [connectedRfp?.lastSyncedAt]);

  async function handleRefresh() {
    if (!onRefresh) return;
    
    try {
      setRefreshing(true);
      await onRefresh();
      notificationStore.success('Cvent data refreshed successfully', {
        title: 'Refresh Complete',
      });
    } catch (error) {
      console.error('Failed to refresh Cvent data:', error);
      notificationStore.error('Failed to refresh Cvent data. Please try again.', {
        title: 'Refresh Error',
      });
    } finally {
      setRefreshing(false);
    }
  }

  async function handleRfpSelect(rfp: CventRfp) {
    try {
      setConnecting(true);
      const result = await cventService.connectEventToRfp(eventId, rfp.id);
      
      notificationStore.success(`Connected to ${rfp.name}`, {
        title: 'Cvent Connection Successful',
      });
      
      // Pass match results to parent so it can display them immediately
      onRfpConnected(result.matchResults);
    } catch (error) {
      console.error('Failed to connect to Cvent RFP:', error);
      notificationStore.error('Failed to connect to Cvent RFP. Please try again.', {
        title: 'Connection Error',
      });
    } finally {
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    try {
      setDisconnecting(true);
      const result = await cventService.disconnectEventFromRfp(eventId);
      
      notificationStore.success(
        `Disconnected from Cvent. ${result.removedConnections} venue ${result.removedConnections === 1 ? 'connection' : 'connections'} removed.`,
        {
          title: 'Disconnected Successfully',
        }
      );
      
      setDisconnectDialogOpen(false);
      onRfpDisconnected();
    } catch (error) {
      console.error('Failed to disconnect from Cvent RFP:', error);
      notificationStore.error('Failed to disconnect from Cvent RFP. Please try again.', {
        title: 'Disconnection Error',
      });
    } finally {
      setDisconnecting(false);
    }
  }

  const hasSubmittedProposals = submittedProposalCount > 0;

  return (
    <>
      <div className="border-b bg-white px-4 py-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          {/* Left side: Back button + Title */}
          <div className="flex items-start gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0 mt-0.5"
              aria-label="Back to Research"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Track RFPs in Cvent
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {totalVenues} {totalVenues === 1 ? 'venue' : 'venues'} across{' '}
                {destinationCount} {destinationCount === 1 ? 'destination' : 'destinations'}
              </p>
            </div>
          </div>
          
          {/* Right side: Connect button (when not connected) + Continue to Selection */}
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap sm:flex-nowrap">
            {!connectedRfp && (
              <Button
                size="sm"
                variant="outline"
                leftIcon={<LinkIcon className="w-4 h-4" />}
                onClick={() => setSearchOpen(true)}
                loading={connecting}
              >
                <span className="hidden sm:inline">Connect to Cvent</span>
                <span className="sm:hidden">Connect</span>
              </Button>
            )}

            {/* Continue to Selection */}
            {onContinueToSelection && (
              <SimpleTooltip
                content={
                  !hasSubmittedProposals 
                    ? "Wait for venues to submit proposals" 
                    : `Review ${submittedProposalCount} submitted proposal${submittedProposalCount > 1 ? 's' : ''}`
                }
              >
                <span>
                  <Button
                    variant={hasSubmittedProposals ? "primary" : "outline"}
                    size="sm"
                    rightIcon={<ArrowRight className="w-4 h-4" />}
                    onClick={onContinueToSelection}
                    disabled={!hasSubmittedProposals}
                    className={cn(
                      "relative transition-all",
                      hasSubmittedProposals && "shadow-sm hover:shadow-md"
                    )}
                  >
                    <span className="hidden sm:inline">Selection</span>
                    <span className="sm:hidden">Select</span>
                    {hasSubmittedProposals && (
                      <Badge 
                        variant="primary-inverted" 
                        className="ml-2 px-1.5 py-0 text-xs h-5 min-w-[20px] flex items-center justify-center"
                      >
                        {submittedProposalCount}
                      </Badge>
                    )}
                  </Button>
                </span>
              </SimpleTooltip>
            )}
          </div>
        </div>
        
        {/* Cvent Connection Status - Multiple rows */}
        {connectedRfp && (
          <div className="bg-success-50 border border-success-200 rounded-md px-3 py-2.5 mt-3">
            {/* First row: Connection status and RFP info */}
            <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap mb-1.5">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <CheckCircle2 className="w-4 h-4 text-success-600 flex-shrink-0" />
                <span className="text-sm font-medium text-success-800">Connected to Cvent</span>
                <span className="hidden sm:inline text-success-600">·</span>
                <span className="text-sm text-success-700 truncate hidden sm:inline" title={connectedRfp.name}>
                  {connectedRfp.name}
                </span>
                <Badge variant="outline" className="text-xs font-mono bg-success-100/50 text-success-700 border-success-300 flex-shrink-0">
                  {connectedRfp.code}
                </Badge>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {onRefresh && (
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<RefreshCw className={cn("w-3.5 h-3.5", refreshing && "animate-spin")} />}
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="text-success-700 hover:text-success-800 hover:bg-success-100 h-7 px-2 flex-shrink-0"
                  >
                    <span className="hidden sm:inline">Refresh</span>
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<Unlink className="w-3.5 h-3.5" />}
                  onClick={() => setDisconnectDialogOpen(true)}
                  className="text-success-700 hover:text-success-800 hover:bg-success-100 h-7 px-2 flex-shrink-0"
                >
                  Disconnect
                </Button>
              </div>
            </div>
            {/* Second row: Sync status */}
            <div className="text-xs text-success-600 ml-6 mb-1">
              <span>{syncStatus.lastSync}</span>
              {syncStatus.nextSync && (
                <>
                  <span className="mx-1.5">·</span>
                  <span>{syncStatus.nextSync}</span>
                </>
              )}
            </div>
            {/* Third row: Supplier/Venue count */}
            {connectedRfp.supplierCount !== undefined && (
              <div className="text-xs text-success-600 ml-6">
                {connectedRfp.supplierCount} {connectedRfp.supplierCount === 1 ? 'supplier' : 'suppliers'} in Cvent RFP
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <CventRfpSearchCombobox
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={handleRfpSelect}
      />

      {connectedRfp && (
        <DisconnectCventDialog
          isOpen={disconnectDialogOpen}
          onClose={() => setDisconnectDialogOpen(false)}
          onConfirm={handleDisconnect}
          rfpName={connectedRfp.name}
          loading={disconnecting}
        />
      )}
    </>
  );
}
